import React, { useEffect, useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Video, Compass, GraduationCap, DoorOpen, Sprout, ArrowRight, ArrowUp, ArrowLeft, Sparkles } from 'lucide-react';
import { LazySection } from '../components/UI/LazySection';
import { speakersData } from './GreenSpeakerDetail';
// Slides for the auto-advancing "what this is about" carousel
const programSlides = [{
  eyebrow: 'Why It Matters',
  title: 'The sustainability space is moving fast.',
  body: 'But many aspiring professionals are still stuck on the basics — career pathways, practical direction, mentorship, and how to navigate the industry intentionally.'
}, {
  eyebrow: 'What You Get',
  title: 'Real journeys, not theory.',
  body: 'Sustainability pros will share their personal journeys — the wins, the wrong turns, and how they actually broke into the field.'
}, {
  eyebrow: 'What You Get',
  title: 'Lessons learned the hard way.',
  body: 'Practical insights you can apply immediately — no fluff, no jargon. Just what works.'
}, {
  eyebrow: 'What You Get',
  title: 'Industry insights from the inside.',
  body: 'What hiring managers, consultants, and analysts actually look for — straight from people doing the work.'
}, {
  eyebrow: 'What You Get',
  title: 'Honest advice for finding your footing.',
  body: 'Real talk on how to position yourself, what skills compound, and how to build relationships that matter.'
}, {
  eyebrow: 'The Goal',
  title: 'Make sustainability careers feel clearer.',
  body: 'More accessible. Less intimidating. Built on real conversations between people who have been where you are — and people who have made it through.'
}];
export const GreenEvent = () => {
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Auto-advance the slider
  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setCurrentSlide(p => (p + 1) % programSlides.length);
    }, 5000);
    return () => clearInterval(id);
  }, [isPaused]);
  const scrollToForm = () => {
    const formSection = document.getElementById('registration-form');
    if (formSection) {
      formSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Hero image collage: founder, host, and 3 trainers (headshot-focused)
  const heroPeople = [{
    id: 'andrew-igbineweka',
    name: speakersData['andrew-igbineweka'].name,
    tag: 'Founder',
    data: speakersData['andrew-igbineweka'],
    featured: true
  }, {
    id: 'bella-ojoka',
    name: speakersData['bella-ojoka'].name,
    tag: 'Host & Convener',
    data: speakersData['bella-ojoka']
  }, {
    id: 'sunday-ojo',
    name: speakersData['sunday-ojo'].name,
    tag: 'Speaker',
    data: speakersData['sunday-ojo']
  }, {
    id: 'caleb-joshua',
    name: speakersData['caleb-joshua'].name,
    tag: 'Speaker',
    data: speakersData['caleb-joshua']
  }, {
    id: 'ozioma-chinedu',
    name: speakersData['ozioma-chinedu'].name,
    tag: 'Speaker',
    data: speakersData['ozioma-chinedu']
  }];
  return <div className="font-sans text-gray-800 bg-white" data-id="element-3083">
      <style data-id="element-3084">
        {`
          @keyframes green-float-slow {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-30px) rotate(8deg); }
          }
          @keyframes green-float-reverse {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(25px) rotate(-6deg); }
          }
          @keyframes green-pulse-glow {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @keyframes green-reveal {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .green-blob-1 { animation: green-float-slow 12s ease-in-out infinite; }
          .green-blob-2 { animation: green-float-reverse 14s ease-in-out infinite; }
          .green-blob-3 { animation: green-float-slow 10s ease-in-out infinite 2s; }
          .green-pulse-dot { animation: green-pulse-glow 2.5s ease-in-out infinite; }
          .green-reveal { animation: green-reveal 0.6s ease-out both; }
          @media (prefers-reduced-motion: reduce) {
            .green-blob-1, .green-blob-2, .green-blob-3,
            .green-pulse-dot, .green-reveal {
              animation: none !important;
            }
          }
        `}
      </style>

      {/* HERO SECTION — Left: copy. Right: speaker headshots collage */}
      <section className="relative pt-10 pb-14 sm:pt-16 sm:pb-20 md:pt-20 md:pb-24 overflow-hidden bg-navy text-white" data-id="element-3085">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-id="element-3086">
          <div className="green-blob-1 absolute -top-32 -right-32 w-[300px] sm:w-[450px] h-[300px] sm:h-[450px] rounded-full bg-secondary/20 blur-3xl" data-id="element-3087"></div>
          <div className="green-blob-2 absolute -bottom-32 -left-32 w-[300px] sm:w-[450px] h-[300px] sm:h-[450px] rounded-full bg-gold/15 blur-3xl" data-id="element-3088"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjA1KSIvPjwvc3ZnPg==')] opacity-50" data-id="element-3089"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-7xl" data-id="element-3090">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center" data-id="element-3091">
            {/* LEFT: All textual info */}
            <div className="lg:col-span-7 text-left green-reveal" data-id="element-3092">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 border border-white/20 backdrop-blur-sm text-white font-semibold rounded-full text-[10px] sm:text-sm mb-5 sm:mb-7 tracking-wide uppercase" data-id="element-3093">
                <span className="green-pulse-dot w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gold" aria-hidden="true" data-id="element-3094"></span>
                The Sustainability Networking Salon
              </div>

              <h1 className="font-bold mb-4 sm:mb-5 leading-[1.05] text-white font-heading" style={{
              fontSize: 'clamp(2rem, 5.5vw, 4.25rem)'
            }} data-id="element-3095">
                Finding Your Path in{' '}
                <span className="relative inline-block" data-id="element-3096">
                  <span className="relative z-10 text-gold" data-id="element-3097">
                    Sustainability
                  </span>
                  <span className="absolute bottom-1 left-0 w-full h-3 sm:h-4 bg-secondary/40 -z-0 -skew-x-6" aria-hidden="true" data-id="element-3098"></span>
                </span>
              </h1>

              <p className="text-gray-200 mb-6 sm:mb-8 leading-relaxed font-light max-w-2xl" style={{
              fontSize: 'clamp(0.95rem, 1.6vw, 1.25rem)'
            }} data-id="element-3099">
                A real-talk session on how to gain clarity, build the right
                skills, and move forward with direction.
              </p>

              {/* Event details */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-0 mb-7 sm:mb-9 max-w-2xl bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-2 sm:p-3 green-reveal" style={{
              animationDelay: '150ms'
            }} data-id="element-3100">
                <div className="flex-1 flex items-center justify-start gap-3 px-3 py-2 sm:py-2.5" data-id="element-3101">
                  <Calendar className="w-5 h-5 text-gold flex-shrink-0" aria-hidden="true" data-id="element-3102" />
                  <div className="text-left" data-id="element-3103">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400" data-id="element-3104">
                      Date
                    </p>
                    <p className="font-semibold text-white text-sm" data-id="element-3105">
                      Fri, 22 May 2026
                    </p>
                  </div>
                </div>
                <div className="hidden sm:block w-px bg-white/15" aria-hidden="true" data-id="element-3106"></div>
                <div className="flex-1 flex items-center justify-start gap-3 px-3 py-2 sm:py-2.5" data-id="element-3107">
                  <Clock className="w-5 h-5 text-gold flex-shrink-0" aria-hidden="true" data-id="element-3108" />
                  <div className="text-left" data-id="element-3109">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400" data-id="element-3110">
                      Time
                    </p>
                    <p className="font-semibold text-white text-sm" data-id="element-3111">
                      7:00 PM WAT
                    </p>
                  </div>
                </div>
                <div className="hidden sm:block w-px bg-white/15" aria-hidden="true" data-id="element-3112"></div>
                <div className="flex-1 flex items-center justify-start gap-3 px-3 py-2 sm:py-2.5" data-id="element-3113">
                  <Video className="w-5 h-5 text-gold flex-shrink-0" aria-hidden="true" data-id="element-3114" />
                  <div className="text-left" data-id="element-3115">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400" data-id="element-3116">
                      Where
                    </p>
                    <p className="font-semibold text-white text-sm" data-id="element-3117">
                      Google Meet
                    </p>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 green-reveal" style={{
              animationDelay: '300ms'
            }} data-id="element-3118">
                <button onClick={scrollToForm} className="group min-h-[48px] w-full sm:w-auto px-7 sm:px-8 py-3.5 text-base sm:text-lg bg-gold hover:bg-gold/90 text-navy font-bold rounded-lg shadow-xl shadow-gold/20 hover:shadow-gold/40 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy inline-flex items-center justify-center gap-2" data-id="element-3119">
                  Register Now
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-3120" />
                </button>
                <a href="https://bit.ly/49o7S80" target="_blank" rel="noopener noreferrer" className="group min-h-[48px] w-full sm:w-auto px-7 sm:px-8 py-3.5 text-base sm:text-lg font-semibold text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-lg transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy inline-flex items-center justify-center gap-2" data-id="element-3121">
                  Join the Conversation
                  <Sparkles className="w-5 h-5 text-gold transition-transform group-hover:rotate-12" aria-hidden="true" data-id="element-3122" />
                </a>
              </div>
            </div>

            {/* RIGHT: Speaker headshot tiles — uniform, headshot-cropped */}
            <div className="lg:col-span-5 green-reveal" style={{
            animationDelay: '200ms'
          }} data-id="element-3123">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-2.5 sm:gap-3 max-w-md sm:max-w-2xl lg:max-w-none mx-auto" data-id="element-3124">
                {heroPeople.map((person, i) => {
                const accent: 'gold' | 'secondary' | 'white' = person.tag === 'Founder' ? 'gold' : person.tag === 'Host & Convener' ? 'secondary' : 'white';
                // Last tile centers itself on mobile when odd count
                const isLast = i === heroPeople.length - 1;
                return <HeroPersonTile key={person.id} to={`/green/speaker/${person.id}`} src={person.data.image} name={person.name} tag={person.tag} accent={accent} className={`aspect-[3/4] ${isLast ? 'col-span-2 w-1/2 justify-self-center sm:col-span-1 sm:w-auto sm:justify-self-stretch' : ''}`} priority={i === 0} data-id="element-3125" />;
              })}
              </div>
              <p className="text-center text-xs text-gray-400 mt-3 italic" data-id="element-3126">
                Tap any portrait to read their bio
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOSTED BY STRIP */}
      <section className="bg-secondary text-navy py-3 sm:py-5 border-y-2 border-navy/10" data-id="element-3127">
        <div className="container mx-auto px-4 text-center" data-id="element-3128">
          <p className="text-xs sm:text-base font-bold tracking-wide" data-id="element-3129">
            <span aria-hidden="true" data-id="element-3130">🌍</span> Hosted by{' '}
            <Link to="/" className="underline underline-offset-4 hover:text-navy/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded" data-id="element-3131">
              The Networking Salon
            </Link>{' '}
            <span className="hidden sm:inline" data-id="element-3132">
              — building rooms where careers are made.
            </span>
          </p>
        </div>
      </section>

      {/* PROGRAM SLIDER — auto-advancing */}
      <LazySection minHeight="500px" rootMargin="200px" data-id="element-3133">
        <section className="py-14 sm:py-20 md:py-28 bg-white" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-3134">
          <div className="container mx-auto px-4 sm:px-6 max-w-4xl" data-id="element-3135">
            <div className="text-center mb-8 sm:mb-12" data-id="element-3136">
              <span className="inline-block text-secondary font-bold tracking-[0.2em] uppercase text-[10px] sm:text-sm mb-3" data-id="element-3137">
                About the Program
              </span>
              <h2 className="font-bold text-navy font-heading leading-tight" style={{
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)'
            }} data-id="element-3138">
                What this brings you
              </h2>
            </div>

            {/* Slide viewport */}
            <div className="relative bg-navy rounded-3xl overflow-hidden shadow-2xl" data-id="element-3139">
              <div className="absolute top-0 right-0 w-72 h-72 bg-gold/10 rounded-full blur-3xl" aria-hidden="true" data-id="element-3140"></div>
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-secondary/15 rounded-full blur-3xl" aria-hidden="true" data-id="element-3141"></div>

              <div className="relative min-h-[300px] sm:min-h-[340px] md:min-h-[360px] flex items-center" data-id="element-3142">
                {programSlides.map((slide, i) => <div key={i} className={`absolute inset-0 p-7 sm:p-12 md:p-16 flex flex-col justify-center transition-all duration-700 ease-out ${i === currentSlide ? 'opacity-100 translate-x-0' : i < currentSlide ? 'opacity-0 -translate-x-8 pointer-events-none' : 'opacity-0 translate-x-8 pointer-events-none'}`} aria-hidden={i !== currentSlide} data-id="element-3143">
                    <span className="text-gold font-bold tracking-[0.2em] uppercase text-[10px] sm:text-xs mb-3 sm:mb-4" data-id="element-3144">
                      {slide.eyebrow}
                    </span>
                    <h3 className="text-white font-bold font-heading mb-3 sm:mb-5 leading-tight" style={{
                  fontSize: 'clamp(1.25rem, 3.5vw, 2.25rem)'
                }} data-id="element-3145">
                      {slide.title}
                    </h3>
                    <p className="text-gray-200 leading-relaxed max-w-2xl" style={{
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.125rem)'
                }} data-id="element-3146">
                      {slide.body}
                    </p>
                  </div>)}
              </div>

              {/* Slide indicators */}
              <div className="relative flex items-center justify-center gap-2 pb-5 sm:pb-6" data-id="element-3147">
                {programSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} aria-label={`Go to slide ${i + 1}`} aria-current={i === currentSlide} className={`min-w-[24px] min-h-[24px] flex items-center justify-center group focus-visible:outline-none`} data-id="element-3148">
                    <span className={`block rounded-full transition-all duration-300 ${i === currentSlide ? 'w-8 h-1.5 bg-gold' : 'w-1.5 h-1.5 bg-white/30 group-hover:bg-white/60'}`} data-id="element-3149"></span>
                  </button>)}
              </div>

              {/* Prev/Next */}
              <button onClick={() => setCurrentSlide(p => (p - 1 + programSlides.length) % programSlides.length)} aria-label="Previous slide" className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-3150">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-3151" />
              </button>
              <button onClick={() => setCurrentSlide(p => (p + 1) % programSlides.length)} aria-label="Next slide" className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-3152">
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-3153" />
              </button>
            </div>
          </div>
        </section>
      </LazySection>

      {/* WHAT TO EXPECT */}
      <LazySection minHeight="400px" rootMargin="200px" data-id="element-3154">
        <section className="py-14 sm:py-20 md:py-28 bg-gray-50" data-id="element-3155">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl" data-id="element-3156">
            <div className="text-center mb-10 sm:mb-14" data-id="element-3157">
              <span className="inline-block text-secondary font-bold tracking-[0.2em] uppercase text-[10px] sm:text-sm mb-3" data-id="element-3158">
                Interactive Q&amp;A
              </span>
              <h2 className="font-bold text-navy mb-3 font-heading leading-tight" style={{
              fontSize: 'clamp(1.75rem, 5vw, 3rem)'
            }} data-id="element-3159">
                Not Another Webinar
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" style={{
              fontSize: 'clamp(0.95rem, 1.8vw, 1.25rem)'
            }} data-id="element-3160">
                Bring your real questions. This is a conversation — not a
                lecture.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-3161">
              {[{
              icon: Compass,
              title: 'Career pathways in sustainability',
              num: '01'
            }, {
              icon: GraduationCap,
              title: 'Skills and competencies that matter',
              num: '02'
            }, {
              icon: DoorOpen,
              title: 'Breaking into the industry',
              num: '03'
            }, {
              icon: Sprout,
              title: 'Gaining experience early in your career',
              num: '04'
            }].map((item, i) => <div key={i} className="group relative bg-white p-5 sm:p-7 md:p-8 rounded-2xl border-2 border-gray-100 hover:border-navy transition-all duration-300 hover:-translate-y-2 active:scale-[0.98] hover:shadow-2xl cursor-default overflow-hidden" data-id="element-3162">
                  <span className="absolute top-3 right-4 text-4xl sm:text-5xl font-black text-gray-100 group-hover:text-gold/30 transition-colors" aria-hidden="true" data-id="element-3163">
                    {item.num}
                  </span>
                  <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-navy rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-gold transition-colors" data-id="element-3164">
                    <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white group-hover:text-navy transition-colors" aria-hidden="true" data-id="element-3165" />
                  </div>
                  <h3 className="relative text-base sm:text-lg md:text-xl font-bold text-navy leading-snug" data-id="element-3166">
                    {item.title}
                  </h3>
                </div>)}
            </div>
          </div>
        </section>
      </LazySection>

      {/* REGISTRATION FORM */}
      <LazySection minHeight="800px" rootMargin="200px" data-id="element-3167">
        <section id="registration-form" className="py-14 sm:py-20 md:py-28 bg-navy relative overflow-hidden" data-id="element-3168">
          <div className="absolute top-0 right-0 w-72 sm:w-96 h-72 sm:h-96 bg-secondary/10 rounded-full blur-3xl" aria-hidden="true" data-id="element-3169"></div>
          <div className="absolute bottom-0 left-0 w-72 sm:w-96 h-72 sm:h-96 bg-gold/10 rounded-full blur-3xl" aria-hidden="true" data-id="element-3170"></div>

          <div className="container mx-auto px-4 sm:px-6 max-w-4xl relative z-10" data-id="element-3171">
            <div className="text-center mb-8 sm:mb-12" data-id="element-3172">
              <span className="inline-block text-gold font-bold tracking-[0.2em] uppercase text-[10px] sm:text-sm mb-3" data-id="element-3173">
                Register
              </span>
              <h2 className="font-bold text-white mb-3 font-heading leading-tight" style={{
              fontSize: 'clamp(1.75rem, 5vw, 3rem)'
            }} data-id="element-3174">
                Secure Your Spot
              </h2>
              <p className="text-gray-300 max-w-2xl mx-auto" style={{
              fontSize: 'clamp(0.95rem, 1.8vw, 1.25rem)'
            }} data-id="element-3175">
                Fill out the form below. You'll get the Google Meet link via
                email.
              </p>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden p-2 sm:p-4" data-id="element-3176">
              <iframe src="https://docs.google.com/forms/d/e/1FAIpQLSe08lOpTHAV5jijckv46fJkZYIOnM5jtlzGzZuAmxY4ghISiQ/viewform?embedded=true" width="100%" height="800" frameBorder="0" marginHeight={0} marginWidth={0} className="w-full rounded-xl sm:rounded-2xl" title="Sustainability Networking Salon Registration Form" loading="lazy" data-id="element-3177">
                Loading…
              </iframe>
            </div>
          </div>
        </section>
      </LazySection>

      {/* Back to top */}
      <button onClick={scrollToTop} aria-label="Back to top" className={`fixed bottom-6 right-6 z-40 min-w-[48px] min-h-[48px] w-12 h-12 rounded-full bg-gold text-navy shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 ${showTopBtn ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`} data-id="element-3178">
        <ArrowUp className="w-5 h-5" aria-hidden="true" data-id="element-3179" />
      </button>
    </div>;
};
// Headshot-focused tile used in the hero collage
type HeroTileProps = {
  to: string;
  src: string;
  name: string;
  tag: string;
  accent: 'gold' | 'secondary' | 'white';
  className?: string;
  priority?: boolean;
};
const HeroPersonTile = ({
  to,
  src,
  name,
  tag,
  accent,
  className,
  priority
}: HeroTileProps) => {
  const accentClasses = accent === 'gold' ? 'ring-2 ring-gold' : accent === 'secondary' ? 'ring-2 ring-secondary' : 'ring-1 ring-white/20';
  const tagClasses = accent === 'gold' ? 'bg-gold text-navy' : accent === 'secondary' ? 'bg-secondary text-white' : 'bg-white/95 text-navy';
  return <Link to={to} className={`group relative rounded-xl sm:rounded-2xl overflow-hidden bg-navy ${accentClasses} ${className} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy transition-transform duration-300 hover:scale-[1.02] active:scale-[0.99]`} aria-label={`Read bio of ${name}, ${tag}`} data-id="element-3180">
      <img src={src} alt={`${name}, ${tag}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" style={{
      objectPosition: '50% 20%'
    }} loading="eager" decoding="async" {...priority ? {
      fetchpriority: 'high' as any
    } : {}} data-id="element-3181" />

      {/* Always-on bottom gradient + name */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy via-navy/70 to-transparent p-2 sm:p-3" aria-hidden="true" data-id="element-3182">
        <p className="text-white font-bold text-[10px] sm:text-xs md:text-sm leading-tight truncate" data-id="element-3183">
          {name}
        </p>
        <span className={`inline-block ${tagClasses} text-[8px] sm:text-[9px] md:text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-0.5`} data-id="element-3184">
          {tag}
        </span>
      </div>

      {/* Hover overlay with View Bio pill */}
      <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/55 transition-colors duration-300 flex items-center justify-center" data-id="element-3185">
        <span className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 bg-gold text-navy font-bold py-1.5 px-3 sm:py-2 sm:px-4 rounded-full inline-flex items-center shadow-lg text-[10px] sm:text-xs" data-id="element-3186">
          View Bio
          <ArrowRight className="ml-1.5 w-3 h-3" aria-hidden="true" data-id="element-3187" />
        </span>
      </div>
    </Link>;
};